<script setup>
import TheCustomerMetaNavigation from '@/components/basic/atoms/TheCustomerMetaNavigation.vue'
import UspBar from '@/components/basic/atoms/UspBar.vue'
import TheHeader from '@/components/basic/organisms/TheHeader.vue'
import TheFilter from '@/components/category/organisms/TheFilter.vue'
import { ASSET_DOWNLOAD_URL } from '@/constants/CollectionPageConstants'
import {
  containsPrivateInfo,
  NO_INDEX_FOLLOW
} from '@/services/helpers/MetaHelper.js'
import { useCollectionPageStore } from '@/stores/collectionPage'
import { useSearchConfig } from '@/stores/searchConfig'
import { useHead } from '@unhead/vue'
import { computed, defineAsyncComponent, nextTick, watch } from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import { useRecommendationStore } from '../stores/recommendations.js'

const LazyComponent = defineAsyncComponent(
  () => import('@/components/basic/atoms/LazyComponent.vue')
)
const AppBanner = defineAsyncComponent(
  () => import('@/components/basic/molecules/AppBanner.vue')
)
const TheFooter = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheFooter.vue')
)

const DUMMY_CATEGORY_ID = 1

const collectionPageStore = useCollectionPageStore()
const { fetchCollectionPage } = collectionPageStore
const searchConfig = useSearchConfig()
const route = useRoute()

const page = computed(() => collectionPageStore.page)
const category = computed(() => searchConfig.category)
const title = computed(
  () => page.value?.meta_title || page.value?.title || ''
)

const assetUrl = (id) => `${ASSET_DOWNLOAD_URL}${id}/download`
const hasTeaser = computed(
  () =>
    page.value?.desktop_asset_id ||
    page.value?.tablet_asset_id ||
    page.value?.mobile_asset_id
)
const teaserFallbackUrl = computed(() => {
  const p = page.value
  return assetUrl(p?.desktop_asset_id || p?.tablet_asset_id || p?.mobile_asset_id)
})

const recommendations = useRecommendationStore()
recommendations.fetch()

fetchCollectionPage(route.params.id)
searchConfig.fetchCategory(DUMMY_CATEGORY_ID)

watch(
  () => route.params.id,
  async (newId, oldId) => {
    if (oldId && newId && newId !== oldId) {
      searchConfig.resetConfig()
      await nextTick()
      await fetchCollectionPage(newId)
      await searchConfig.fetchCategory(DUMMY_CATEGORY_ID)
    }
  }
)

onBeforeRouteLeave(() => {
  collectionPageStore.$reset()
  searchConfig.resetConfig()
})

useHead({
  title,
  meta: [
    {
      name: 'robots',
      content: computed(() =>
        containsPrivateInfo() ? NO_INDEX_FOLLOW : page.value?.robots
      )
    },
    {
      name: 'description',
      content: computed(() => page.value?.meta_description)
    }
  ],
  link: [
    {
      rel: 'canonical',
      href: computed(() => page.value?.page_url || route.path)
    }
  ]
})
</script>

<template>
  <div
    class="position-relative"
    id="content"
    data-elbglobals="page_type:collection"
  >
    <AppBanner />
    <UspBar />
    <TheCustomerMetaNavigation />
    <TheHeader class="position-relative" />
    <main id="page-template">
      <div class="v-container my-5">
        <div class="v-col-12 py-5">
          <h1
            v-if="page.title"
            class="font-garbata text-center font-size-xxxxl font-size-md-xxxxxl font-weight-bold c-winered"
          >
            {{ page.title }}
          </h1>
        </div>
      </div>
      <div v-if="hasTeaser" class="v-container mb-5">
        <picture>
          <source
            v-if="page.desktop_asset_id"
            :srcset="assetUrl(page.desktop_asset_id)"
            media="(min-width: 1280px)"
          />
          <source
            v-if="page.tablet_asset_id"
            :srcset="assetUrl(page.tablet_asset_id)"
            media="(min-width: 768px)"
          />
          <source
            v-if="page.mobile_asset_id"
            :srcset="assetUrl(page.mobile_asset_id)"
            media="(max-width: 767px)"
          />
          <img
            :src="teaserFallbackUrl"
            :alt="page.title"
            class="w-100 rounded"
          />
        </picture>
      </div>
      <TheFilter v-if="category.code" />
    </main>
    <LazyComponent id="footer" :delay="2000">
      <TheFooter />
    </LazyComponent>
  </div>
</template>
